import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { AccountingType } from 'v2-types/order';

import useAlert from 'utils/alert';
import { useGetListings } from 'hooks/useGetListings';
import { getOrder, updateOrderPolicy } from './query';
import { cdLetters, hudNumbers } from 'views/orders/charges/utils';
import { useAccountingUpdateSocketEvent } from 'hooks/useAccountingUpdateFromSocket';
import { ChargeFormEntry } from 'views/orders/charges/components/GeneralChargesTable/types';
import { HUD_1 } from 'utils/constants';

export type ConfirmationModal = 'confirmationModal';

export type ModalState =
	| {
		modal: 'deleteDocument' | 'editDocument';
		charge: ChargeFormEntry;
		rowIndex: number;
	}
	| {
		modal: '';
		charge?: ChargeFormEntry;
	}
	| {
		modal: 'addDocument';
		charge?: ChargeFormEntry;
		rowIndex?: number;
	}
	| {
		modal: 'editSplit';
		charges: ChargeFormEntry[];
		kind: 'Payee' | 'Payer' | 'Tax';
	}
	| {
		modal: ConfirmationModal;
		onConfirm: () => void;
		confirmationMessage: string;
		confirmationTitle: string;
		isLoading: boolean;
	};

export type OpenConfirmationModalType = {
	confirmationMessage: string;
	confirmationTitle: string;
	onConfirm: () => void;
};

type Params =
	| {
		orderType: string;
		currentLetter: string;
		accountingFilter?: never;
	}
	| {
		orderType: string;
		accountingFilter: string;
		currentLetter?: never;
	};

export const useCharges = ({ orderType, currentLetter, accountingFilter }: Params) => {
  const [accounting, setAccounting] = useState<AccountingType[]>([]);
  const { id: orderId } = useParams<{ id: string }>();
  const [hideTax, setHideTax] = useState(true);
  const showAlert = useAlert();
  const [showNewInfoAlert, setShowNewInfoAlert] = useState<boolean>(false);
  const queryCache = useQueryClient();
  const [disclosureSection, setDisclosureSection] = useState<string>('');

  const reloadAccounting = () => queryCache.invalidateQueries(['accounting-order', orderId]);
  const { data: lisitingResponse } = useGetListings({
    code: 'charge_descriptions',
  });

  const { data: lisitingLocked } = useGetListings({
    code: 'locked_accounting_amounts',
  });

  const textPredectiveCharges = lisitingResponse?.getListing.entries || [];
  const lockedCharges = lisitingLocked?.getListing.entries || [];

  const [modalState, setModalState] = useState<ModalState>({
    modal: '',
  });

  useAccountingUpdateSocketEvent({
    callback: () => reloadAccounting(),
  });

  const updateModalState = (newModalState: ModalState) => setModalState(newModalState);
  const handleCloseModal = () => {
    updateModalState({ modal: '' });
  };

  const openConfirmationModal = ({
    confirmationMessage,
    confirmationTitle,
    onConfirm,
  }: OpenConfirmationModalType) => {
    updateModalState({
      modal: 'confirmationModal',
      onConfirm: () => {
        setModalState((prevValues) => ({
          ...prevValues,
          isLoading: true,
        }));
        onConfirm();
      },
      isLoading: false,
      confirmationMessage,
      confirmationTitle,
    });
  };

  const { mutate: updatePolicyMutation, ...updateOrderPoliciyResponse } = useMutation(
    updateOrderPolicy,
    {
      onSuccess: (data) => {
        showAlert('Title Charges were successfully updated', 'success');
        handleCloseModal();
      },
    }
  );

  const handleToggleDisclosureSection = useCallback(
    () =>
      updatePolicyMutation({
        id: orderId,
        kind: 'lenders',
        data: { disclosure_section: disclosureSection === 'B' ? 'C' : 'B' },
      }),
    [disclosureSection, orderId, updatePolicyMutation]
  );

  const filterCharges = (charges: AccountingType[]) => {
    if (orderType === HUD_1 && currentLetter) {
      return charges.filter((charge) => {
        const { number, code } = charge;
        if (charge.letter === 'L') {
          if (number !== null) {
            if (currentLetter === '700' && number > 700 && number < 800) return true;
            if (currentLetter === '800' && number >= 800 && number < 900) return true;
            if (currentLetter === '900' && number >= 900 && number < 1000) return true;
            if (currentLetter === '1000' && number >= 1000 && number < 1100) return true;
            if (currentLetter === '1100' && number >= 1100 && number < 1200) return true;
            if (currentLetter === '1200' && number >= 1200 && number < 1300) return true;
            if (currentLetter === '1300' && number >= 1300 && number < 1400) return true;
          }

          if (code === 'document' && currentLetter === '1200') return true;
        }
        return false;
      });
    }

    if (currentLetter) {
      return charges.filter((charge) => charge.letter === currentLetter && (charge.number !== null || charge.code === 'document'));
    }
    return charges;
  };

  const canFetchAccounting =
		!!orderId &&
		(currentLetter ? cdLetters.concat(hudNumbers).includes(currentLetter) : !!accountingFilter); // Aseguramos que sea booleano

  const { isLoading, isFetching } = useQuery(
    ['accounting-order', orderId, currentLetter, accountingFilter],
    () =>
      getOrder(orderId, {
        accounting: accountingFilter ?? (orderType === HUD_1 ? 'L' : currentLetter.toString()),
      }),
    {
      enabled: canFetchAccounting,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          setAccounting(filterCharges(data.getOrder.accounting));
          setDisclosureSection(data.getOrder.lenders_policy.disclosure_section);
        }
        setShowNewInfoAlert(false);
      },
    }
  );

  const updateAccounting = (data: AccountingType[]) => {
    setAccounting(filterCharges(data));
  };

  return {
    orderId,
    accounting: accounting.sort((chargeA, chargeB) => chargeA.number - chargeB.number),
    setAccounting,
    isLoading,
    isFetching,
    updateAccounting,
    hideTax,
    setHideTax,
    handleToggleDisclosureSection,
    disclosureSection,
    policyIsLoading: updateOrderPoliciyResponse.isLoading,
    showNewInfoAlert,
    reloadAccounting,
    filterCharges,
    textPredectiveCharges,
    modalState,
    updateModalState,
    lockedCharges,
    openConfirmationModal,
    handleCloseModal,
  };
};
